import React, { useState, useEffect } from "react";  
import { getFirestore, query, collection, where, deleteDoc, getDocs, addDoc } from "firebase/firestore";  
import { getAuth } from "firebase/auth";  
import {  
  ref,  
  uploadBytes,  
  getDownloadURL,  
  deleteObject,  
  getStorage,  
} from "firebase/storage";  
import {  
  Card,  
  CardContent,  
  CardHeader,  
  CardTitle,  
} from "../components/ui/card";  
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";  
import { Input } from "../components/ui/input";  
import { Button } from "../components/ui/button";  
import { ChevronDown, DownloadCloud, Search, Upload } from "lucide-react";  
import { useToast } from "../hooks/use-toast";  
import ViewToggle from "../components/ui/ViewToggle";  
import { Menubar, MenubarContent, MenubarItem, MenubarMenu, MenubarTrigger } from "../components/ui/menubar";  
import FileGridView from "./files/FileGridView";  
import FileListView from "./files/FileListView";  
import FileEmptyState from "./files/FileEmptyState";  
import FileUploadDialog from "./files/FileUploadDialog";  
import ImportFilesDialog from "./files/ImportFilesDialog";  
import { FileItem, FileType } from "../types/file";  

// The main Files component  
const Files = () => {  
  const [activeTab, setActiveTab] = useState("all");  
  const [searchQuery, setSearchQuery] = useState("");  
  const [isGridView, setIsGridView] = useState(true);  
  const [copiedLinkId, setCopiedLinkId] = useState<string | null>(null);  
  const [sortBy, setSortBy] = useState<"name" | "date" | "size">("date");  
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");  
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);  
  const [importDialogOpen, setImportDialogOpen] = useState(false);  
  const [files, setFiles] = useState<FileItem[]>([]);  
  const { toast } = useToast();  
  const db = getFirestore();  
  const storage = getStorage();  
  const auth = getAuth();  

  // Load all files from Firestore when the component mounts  
  useEffect(() => {  
    const loadFiles = async () => {  
      try {  
        const q = query(collection(db, "files")); // Query to get all files  
        const querySnapshot = await getDocs(q); // Get the documents  
        const filesList:any = querySnapshot.docs.map(doc => ({   
          id: doc.id, // Retrieve the document ID for local state management  
          ...doc.data() // Spread the document data into the object  
        }));  
        setFiles(filesList); // Set the files into state  
      } catch (error) {  
        console.error("Error loading files:", error);  
        toast({  
          title: "Error loading files",  
          description: "There was an issue retrieving the files.",  
          variant: "destructive",  
        });  
      }  
    };  

    loadFiles(); // Invoke the function to load files  
  }, [db, toast]);  // Dependencies for useEffect to avoid unnecessary reruns  

  // Function to sort files by selected criteria  
  const sortFiles = (files: FileItem[]) => {  
    return [...files].sort((a, b) => {  
      if (sortBy === "name") {  
        return sortOrder === "asc" ?  
          a.name.localeCompare(b.name) :  
          b.name.localeCompare(a.name);  
      } else if (sortBy === "date") {  
        return sortOrder === "asc" ?  
          new Date(a.uploadDate).getTime() - new Date(b.uploadDate).getTime() :  
          new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime();  
      } else if (sortBy === "size") {  
        const aSizeNum = parseFloat(a.size.split(" ")[0]);  
        const bSizeNum = parseFloat(b.size.split(" ")[0]);  
        return sortOrder === "asc" ? aSizeNum - bSizeNum : bSizeNum - aSizeNum;  
      }  
      return 0;  
    });  
  };  

  // Filter the files based on the active tab and search query  
  const filteredFiles = sortFiles(files.filter((file) => {  
    const matchesTab =  
      activeTab === "all" ||  
      (activeTab === "documents" && file.type === "document") ||  
      (activeTab === "images" && file.type === "image") ||  
      (activeTab === "videos" && file.type === "video") ||  
      (activeTab === "audio" && file.type === "audio");  

    const matchesSearch = file.name  
      .toLowerCase()  
      .includes(searchQuery.toLowerCase());  

    return matchesTab && matchesSearch;  
  }));  

  // Function to copy the download link of a file  
  const handleCopyLink = (id: string, url: string) => {  
    navigator.clipboard.writeText(url);  
    setCopiedLinkId(id);  

    toast({  
      title: "Link copied!",  
      description: "File link has been copied to clipboard.",  
    });  

    setTimeout(() => {  
      setCopiedLinkId(null);  
    }, 2000);  
  };  

  // Function to delete a file by filename  
  const handleDelete = async (file: any) => {  
    const fileName = file; // Name of the file to delete  
    const fileRef = ref(storage, `files/${fileName}`); // Reference to the file in Firebase Storage  

    try {  
      // Delete the file from Firebase Storage  
      await deleteObject(fileRef);  
      
      // Remove the file from local state  
      setFiles(files.filter(item => item.name !== fileName));  

      // Delete the file metadata from Firestore  
      const q = query(  
        collection(db, "files"),  
        where("name", "==", fileName)  
      );  
      const querySnapshot = await getDocs(q);  
      
      // Use a for...of loop to handle async delete operations  
      for (const doc of querySnapshot.docs) {  
        await deleteDoc(doc.ref); // Await the deletion of each document  
      }  
      
      toast({  
        title: "File deleted",  
        description: "The file has been deleted successfully.",  
        variant: "destructive",  
      });  
    } catch (error) {  
      console.error("Delete failed:", error);  
      toast({  
        title: "Delete failed",  
        description: "Failed to delete the file.",  
        variant: "destructive",  
      });  
    }  
  };  

  // Handle file upload and store it in Firebase  
  const handleFileUpload = async (uploadedFiles: File[]) => {  
    const newFiles: FileItem[] = [];  
    for (const file of uploadedFiles) {  
      const storageRef = ref(storage, `files/${file.name}`);  
      try {  
        await uploadBytes(storageRef, file); // Upload file  
        const url = await getDownloadURL(storageRef); // Get download URL  
        
        // Create a new file item based on uploaded file details  
        newFiles.push({  
          id: crypto.randomUUID(),  
          name: file.name,  
          type: file.type.split('/')[0] as FileType,  
          size: formatFileSize(file.size),  
          uploadDate: new Date().toISOString().split('T')[0],  
          url: url,  
        });  

        // Store file metadata in Firestore  
        await addDoc(collection(db, "files"), {  
          name: file.name,  
          url: url,  
          uploadDate: new Date().toISOString().split('T')[0],  
          type: file.type.split('/')[0],  
          size: file.size,  
        });  

      } catch (error) {  
        console.error("Upload failed:", error);  
        toast({  
          title: "Upload failed",  
          description: error.message,  
          variant: "destructive",  
        });  
      }  
    }  
    // Update states after uploading  
    setFiles((prevFiles) => [...newFiles, ...prevFiles]);  
  };  

  // Handle the completion of the import of files  
  const handleImportComplete = (urls: string[]) => {  
    const newFiles: FileItem[] = urls.map((url) => {  
      const fileName = url.split('/').pop() || "Imported File";  
      const type = determineFileType(fileName);  
      return {  
        id: crypto.randomUUID(),  
        name: fileName,  
        type,  
        size: "Unknown",  
        uploadDate: new Date().toISOString().split('T')[0],  
        url,  
      };  
    });  
    setFiles((prevFiles) => [...newFiles, ...prevFiles]);  
  };  

  // Function to determine the file type based on the file extension  
  const determineFileType = (fileName: string): FileType => {  
    if (fileName.match(/\.(jpeg|jpg|png|gif|bmp|webp)$/i)) return "image";  
    else if (fileName.match(/\.(mp4|avi|mov|wmv|flv|webm)$/i)) return "video";  
    else if (fileName.match(/\.(mp3|wav|ogg|aac|flac)$/i)) return "audio";  
    else if (fileName.match(/\.(pdf|doc|docx|txt|rtf|odt)$/i)) return "document";  
    return "other";  
  };  

  // Function to format file size for display  
  const formatFileSize = (bytes: number) => {  
    if (bytes === 0) return '0 Bytes';  
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];  
    const i = Math.floor(Math.log(bytes) / Math.log(1024));  
    return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i];  
  };  

  // Render the component  
  return (  
    <div className="container py-6 space-y-6 h-full overflow-auto">  
      <div className="flex justify-between items-center">  
        <h1 className="text -2xl font-bold">Files</h1>  
        <div className="flex space-x-2">  
          <Button variant="outline" className="gap-2" onClick={() => setImportDialogOpen(true)}>  
            <DownloadCloud className="h-4 w-4" />  
            Import Files  
          </Button>  
          <Button className="gap-2" onClick={() => setUploadDialogOpen(true)}>  
            <Upload className="h-4 w-4" />  
            Upload New File  
          </Button>  
        </div>  
      </div>  

      <Card className="shadow-sm">  
        <CardHeader className="border-b pb-3">  
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-2 md:space-y-0">  
            <CardTitle>File Manager</CardTitle>  
            <div className="flex space-x-2">  
              <Menubar className="border-none shadow-none p-0">  
                <MenubarMenu>  
                  <MenubarTrigger className="bg-white border rounded-md px-3 py-2 text-sm font-medium gap-2">  
                    Sort by: {sortBy === "name" ? "Name" : sortBy === "date" ? "Date" : "Size"}  
                    <ChevronDown className="h-4 w-4" />  
                  </MenubarTrigger>  
                  <MenubarContent>  
                    <MenubarItem onClick={() => {setSortBy("name"); setSortOrder("asc");}}>  
                      Name (A-Z)  
                    </MenubarItem>  
                    <MenubarItem onClick={() => {setSortBy("name"); setSortOrder("desc");}}>  
                      Name (Z-A)  
                    </MenubarItem>  
                    <MenubarItem onClick={() => {setSortBy("date"); setSortOrder("desc");}}>  
                      Date (Newest)  
                    </MenubarItem>  
                    <MenubarItem onClick={() => {setSortBy("date"); setSortOrder("asc");}}>  
                      Date (Oldest)  
                    </MenubarItem>  
                    <MenubarItem onClick={() => {setSortBy("size"); setSortOrder("desc");}}>  
                      Size (Largest)  
                    </MenubarItem>  
                    <MenubarItem onClick={() => {setSortBy("size"); setSortOrder("asc");}}>  
                      Size (Smallest)  
                    </MenubarItem>  
                  </MenubarContent>  
                </MenubarMenu>  
              </Menubar>  
              <ViewToggle   
                view={isGridView ? "grid" : "list"}   
                onChange={(view) => setIsGridView(view === "grid")}   
              />  
            </div>  
          </div>  
        </CardHeader>  
        <CardContent className="p-0">  
          <div className="p-4 border-b bg-gray-50">  
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">  
              <Tabs   
                defaultValue="all"   
                className="w-full md:w-auto"  
                onValueChange={setActiveTab}  
                value={activeTab}  
              >  
                <TabsList className="grid grid-cols-5 w-full md:w-auto">  
                  <TabsTrigger value="all">All</TabsTrigger>  
                  <TabsTrigger value="documents">Documents</TabsTrigger>  
                  <TabsTrigger value="images">Images</TabsTrigger>  
                  <TabsTrigger value="videos">Videos</TabsTrigger>  
                  <TabsTrigger value="audio">Audio</TabsTrigger>  
                </TabsList>  
              </Tabs>  
              
              <div className="relative w-full md:w-64">  
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />  
                <Input  
                  placeholder="Search files..."  
                  className="pl-8 w-full"  
                  value={searchQuery}  
                  onChange={(e) => setSearchQuery(e.target.value)}  
                />  
              </div>  
            </div>  
          </div>  

          <div className="p-4">  
            {isGridView ? (  
              <FileGridView   
                files={filteredFiles}  
                copiedLinkId={copiedLinkId}  
                onCopyLink={handleCopyLink}  
                onDelete={handleDelete} // Pass the delete handler  
              />  
            ) : (  
              <FileListView   
                files={filteredFiles}  
                copiedLinkId={copiedLinkId}  
                onCopyLink={handleCopyLink}  
                onDelete={handleDelete} // Pass the delete handler  
              />  
            )}  

            {filteredFiles?.length === 0 && (  
              <FileEmptyState searchQuery={searchQuery} />  
            )}  
          </div>  
        </CardContent>  
      </Card>  

      <FileUploadDialog   
        open={uploadDialogOpen}   
        onOpenChange={setUploadDialogOpen}   
        onFileUpload={handleFileUpload}   
      />  
      
      <ImportFilesDialog   
        open={importDialogOpen}   
        onOpenChange={setImportDialogOpen}   
        onImportComplete={handleImportComplete}   
      />  
    </div>  
  );  
};  

export default Files;  